import { useEffect, useState } from 'react';
import { Menu, ArrowUp, FileText, User, Calendar, Tag } from 'lucide-react';
import { Sidebar } from './components/Sidebar';
import { navSections } from './data/navigation';
import * as Intro from './sections/IntroductionSections';
import * as Depl from './sections/DeploymentSections';
import * as Infra from './sections/InfrastructureSections';
import * as Secu from './sections/SecuritySections';
import * as Mgmt from './sections/ManagementSections';
import * as Admin from './sections/AdministrationSections';

function DocumentationPage() {
    const [activeSection, setActiveSection] = useState('executive-summary');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 400);

            const sections = document.querySelectorAll('.doc-section');
            let current = '';
            sections.forEach((section) => {
                const sectionTop = (section as HTMLElement).offsetTop;
                if (window.pageYOffset >= sectionTop - 150) {
                    current = section.getAttribute('id') || '';
                }
            });
            if (current) setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(id);
        }
    };

    const filteredNav = navSections.map(group => ({
        ...group,
        items: group.items.filter(item =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(group => group.items.length > 0);

    return (
        <div className="flex min-h-screen bg-gray-900 text-gray-200 font-sans selection:bg-orange-500/30 selection:text-orange-200">
            <Sidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                activeSection={activeSection}
                scrollTo={scrollToSection}
                filteredNav={filteredNav}
            />

            <main className="flex-1 lg:ml-[260px] relative">
                <header className="lg:hidden sticky top-0 z-[80] flex items-center justify-between px-4 h-14 bg-gray-950/80 backdrop-blur-md border-b border-white/5">
                    <div className="flex items-center gap-2.5">
                        <img src="/owllogopng.png" alt="Logo" className="w-7 h-7" />
                        <span className="font-bold text-[13px] text-gray-100 tracking-tight">Ovul Scanner</span>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-1.5 text-gray-400 hover:text-white transition-colors"
                    >
                        <Menu size={22} />
                    </button>
                </header>

                <div className="relative p-4 overflow-hidden border-b border-white/5">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-xs font-semibold text-orange-400 mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                        Enterprise Documentation
                    </div>

                    <h1 className="text-xl lg:text-2xl font-extrabold tracking-tight leading-tight mb-2 text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400">
                        OvulScanner Enterprise Documentation
                    </h1>

                    <p className="text-[14px] text-gray-200 leading-relaxed mb-5">
                        Your complete guide to setting up, managing, and getting the most out of OvulScanner.
                    </p>

                    <div className="flex flex-wrap gap-4">
                        {[
                            { icon: <FileText size={16} />, label: 'Document ID:', val: 'TBD', color: 'text-yellow-400' },
                            { icon: <User size={16} />, label: 'Prepared By:', val: 'Runners', color: 'text-blue-400' },
                            { icon: <Calendar size={16} />, label: 'Last Updated:', val: 'May 2026', color: 'text-purple-400' },
                            { icon: <Tag size={16} />, label: 'Version:', val: '1.3.0', color: 'text-emerald-400' }
                        ].map(meta => (
                            <div key={meta.label} className="flex items-center gap-1.5 text-[12px]">
                                <span className={meta.color}>{meta.icon}</span>
                                <span className="text-gray-200 font-medium">{meta.label}</span>
                                <span className="text-gray-300">{meta.val}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 mb-16 space-y-12">
                    <Intro.ExecutiveSummary />
                    <Intro.MultiTenantArchitecture />
                    <Intro.PlatformArchitecture />

                    <Depl.AgentGatewayDeployment />
                    <Depl.DiscoveryAssetManagement />
                    <Depl.SNMPMonitoring />
                    <Depl.NetworkDeviceMonitoring />
                    <Depl.TopologyVisualization />

                    <Infra.DigitalTwin />
                    <Infra.EndpointAgents />
                    <Infra.OutOfBandMonitoring />
                    <Infra.CameraMonitoring />
                    <Infra.KubernetesMonitoring />

                    <Secu.FirewallCompliance />
                    <Secu.FirewallSIEM />
                    <Secu.EventEngine />
                    <Secu.EventConfiguration />
                    <Secu.NotificationRules />
                    <Secu.RuleEngine />

                    <Mgmt.PatchManagement />
                    <Mgmt.ReportingAnalytics />
                    <Mgmt.CustomDashboards />
                    <Mgmt.AuditLogs />
                    <Mgmt.Integrations />

                    <Admin.UserManagement />
                    <Admin.SchedulerJobs />
                    <Admin.AssetTagging />
                    <Admin.OSICoverage />
                    <Admin.Appendix />
                </div>

                <footer className="border-t border-white/5 py-12 px-8 mx-auto max-w-4xl opacity-50">
                    <div className="flex justify-between items-center text-[12px]">
                        <p>© 2026 OvulScanner Enterprise. All rights reserved.</p>
                        <div className="flex gap-6">
                            <a href="#" className="hover:text-orange-400 transition-colors">Privacy Policy</a>
                            <a href="#" className="hover:text-orange-400 transition-colors">Terms of Service</a>
                            <a href="#" className="hover:text-orange-400 transition-colors">Support</a>
                        </div>
                    </div>
                </footer>
            </main>

            <button
                className={`fixed bottom-8 right-8 z-50 w-11 h-11 bg-orange-500 hover:bg-orange-400 text-white border-none rounded-full flex items-center justify-center text-xl shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_20px_rgba(249,115,22,0.3)] transition-all duration-300 hover:-translate-y-1 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
                <ArrowUp size={20} />
            </button>
        </div>
    );
}

export default DocumentationPage;
